import React, { useEffect, useState } from "react";
import { ClipboardList, Sparkles } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function TreatmentPlans() {
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ client_id: "", diagnosis: "", presenting_concerns: "", goals: "", modality: "CBT" });
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState("");
  const [autosaveStatus, setAutosaveStatus] = useState("");

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/treatment-plans").then(({ data }) => setPlans(data));
    const params = new URLSearchParams(window.location.search);
    const clientParam = params.get("client");
    if (clientParam) setForm((f) => ({ ...f, client_id: clientParam }));
  }, []);

  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(async () => {
      if (edit && edit !== active.content) {
        setAutosaveStatus("Saving...");
        try {
          await api.put(`/treatment-plans/${active.id}`, { content: edit });
          setAutosaveStatus("Saved " + new Date().toLocaleTimeString());
        } catch {
          setAutosaveStatus("Save failed");
        }
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, [edit, active]);

  const generate = async () => {
    if (!form.client_id || !form.diagnosis) return toast.error("Client and diagnosis required");
    setGen(true);
    try {
      const { data } = await api.post("/treatment-plans/generate", form);
      setPlans([data, ...plans]);
      setActive(data);
      setEdit(data.content);
      toast.success("Treatment plan generated");
    } catch { toast.error("Generation failed"); }
    setGen(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page title="Treatment Plans" subtitle="Evidence-based, measurable, auto-saving every 30 seconds.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.05} testid="plan-composer">
          <h2 className="font-serif text-2xl mb-4">New Plan</h2>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="plan-client-select">
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <input placeholder="Diagnosis (e.g. F33.1 Major Depressive Disorder)" value={form.diagnosis} onChange={set("diagnosis")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="plan-diagnosis" />
            <textarea placeholder="Presenting concerns" value={form.presenting_concerns} onChange={set("presenting_concerns")} rows={3} className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="plan-concerns"></textarea>
            <textarea placeholder="Client-identified goals" value={form.goals} onChange={set("goals")} rows={2} className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="plan-goals"></textarea>
            <select value={form.modality} onChange={set("modality")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="plan-modality">
              {["CBT", "DBT", "ACT", "EMDR", "Psychodynamic", "Family Systems", "Motivational Interviewing"].map(m => <option key={m}>{m}</option>)}
            </select>
            <PrimaryButton onClick={generate} disabled={gen} testid="plan-generate-btn">
              {gen ? "Generating…" : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate Plan</span>}
            </PrimaryButton>
          </div>
        </Card>

        <Card delay={0.1} testid="plan-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Plan Content</h2>
            {autosaveStatus && <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{autosaveStatus}</span>}
          </div>
          {active ? (
            <textarea value={edit} onChange={(e) => setEdit(e.target.value)} rows={20} className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none mf-prose" style={{ background: "var(--mf-muted)" }} data-testid="plan-editor" />
          ) : (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Generate a plan to begin editing.</div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">Existing Plans</h2>
        {plans.length === 0 ? <EmptyState icon={ClipboardList} title="No plans yet" description="Generate your first evidence-based treatment plan." /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {plans.map((p, i) => (
              <div key={p.id} className="mf-card p-5 cursor-pointer mf-fade-in" style={{ animationDelay: `${Math.min(i*0.05, 0.3)}s` }} onClick={() => { setActive(p); setEdit(p.content); }} data-testid={`plan-item-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2"><Pill variant="primary">v{p.version}</Pill><Pill>{p.modality}</Pill></div>
                  <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm line-clamp-3">{p.content?.slice(0, 250)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
